import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DatePicker, Select, Checkbox, Row, Col, Form, ConfigProvider, Button, message, Card, Upload, Modal, Divider, Radio, Space, Typography, InputNumber, Input, Tag } from 'antd';
import { UploadOutlined, SettingOutlined, PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '../styles/Common.css';
import { buildApiUrl } from '../config/api';

dayjs.extend(customParseFormat);
dayjs.locale('ko');

const { Option } = Select;

const COLOR_META = {
  red: { label: '빨강', hex: '#FFC7CE' }, yellow: { label: '노랑', hex: '#FFEB9C' },
  green: { label: '초록', hex: '#C6EFCE' }, blue: { label: '파랑', hex: '#BDD7EE' },
  orange: { label: '주황', hex: '#FCE4D6' }, purple: { label: '보라', hex: '#E4DFEC' },
  none: { label: '없음', hex: 'transparent' },
};
const RULE_TYPE_LABEL = { diff: '퇴근시각 차이', nomatch: '미매칭', absent: '결근', norecord: '무기록' };

async function downloadFile(downloadUrl) {
  try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      // Blob URL 생성
      const blobUrl = URL.createObjectURL(blob);
      
      // 다운로드를 위한 링크 생성
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = '야근 보고서'; // 원하는 파일 이름으로 변경
      
      // 링크를 클릭하여 다운로드 시작
      document.body.appendChild(link);
      link.click();
      
      // 다운로드 후 링크와 Blob URL 해제
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
  } catch (error) {
      console.error('파일 다운로드 중 오류 발생:', error);
  }
}


const AttendanceReport = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isOvertimeChecked, setIsOvertimeChecked] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('All');

  // 에스원(S1) 대조
  const [s1File, setS1File] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [resolutions, setResolutions] = useState({}); // { employee_no: s1EmpId }
  const [conflictOpen, setConflictOpen] = useState(false);

  // 에스원 기준 이름 통일
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [unmatchedList, setUnmatchedList] = useState([]);
  const [s1Pool, setS1Pool] = useState([]);
  const [nameSel, setNameSel] = useState({}); // { employee_no: s1_name }

  // 대조 규칙 (모든 계정 공유)
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [colorOpts, setColorOpts] = useState(Object.keys(COLOR_META));
  const [rulesSaving, setRulesSaving] = useState(false);
  const [ruleHistory, setRuleHistory] = useState([]);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(buildApiUrl('/api/overtime-verify/rules/history'));
      setRuleHistory(data.history || []);
    } catch (e) { setRuleHistory([]); }
  };

  const openRules = async () => {
    try {
      const { data } = await axios.get(buildApiUrl('/api/overtime-verify/rules'));
      setRules(data.rules || []);
      if (data.colors) setColorOpts(data.colors);
      await loadHistory();
      setRulesModalOpen(true);
    } catch (e) {
      message.error('대조 규칙 조회에 실패했습니다.');
    }
  };

  const revertTo = (snapshot) => {
    setRules(snapshot.map((r) => ({ ...r })));
    message.info('이전 규칙을 불러왔습니다. "저장"을 눌러 적용하세요.');
  };
  const updateRule = (i, patch) => setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const moveRule = (i, dir) => setRules((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const addRule = () => setRules((prev) => [...prev, { type: 'diff', min: 0, max: 30, color: 'green', label: '정상' }]);
  const removeRule = (i) => setRules((prev) => prev.filter((_, idx) => idx !== i));
  const saveRules = async () => {
    setRulesSaving(true);
    try {
      await axios.put(buildApiUrl('/api/overtime-verify/rules'), { rules }, { headers: authHeader() });
      message.success('대조 규칙이 저장되었습니다. (모든 계정 공유)');
      await loadHistory();
      setRulesModalOpen(false);
    } catch (e) {
      message.error(e.response?.data?.message || '규칙 저장에 실패했습니다.');
    } finally {
      setRulesSaving(false);
    }
  };

  const openNameUnify = async () => {
    if (!s1File) { message.warning('먼저 에스원 엑셀을 업로드하세요.'); return; }
    setNameLoading(true);
    try {
      const fd = new FormData();
      fd.append('s1file', s1File);
      const { data } = await axios.post(buildApiUrl('/api/overtime-verify/unmatched-employees'), fd);
      setUnmatchedList(data.unmatched || []);
      setS1Pool(data.s1_pool || []);
      // 추천값 기본 선택
      const init = {};
      (data.unmatched || []).forEach((u) => { if (u.recommend) init[u.employee_no] = u.recommend; });
      setNameSel(init);
      setNameModalOpen(true);
    } catch (e) {
      message.error(e.response?.data?.message || '미매칭 직원 조회에 실패했습니다.');
    } finally {
      setNameLoading(false);
    }
  };

  const applyNames = async () => {
    const updates = Object.entries(nameSel).filter(([, v]) => v).map(([employee_no, s1_name]) => ({ employee_no: Number(employee_no), s1_name }));
    if (updates.length === 0) { message.warning('적용할 이름을 선택하세요.'); return; }
    setNameSaving(true);
    try {
      const { data } = await axios.post(buildApiUrl('/api/overtime-verify/apply-names'), { updates });
      message.success(`${data.applied}명 이름이 통일되었습니다.`);
      setNameModalOpen(false);
    } catch (e) {
      message.error(e.response?.data?.message || '이름 저장에 실패했습니다.');
    } finally {
      setNameSaving(false);
    }
  };

  // 대조 요약 엑셀 생성 (동명이인 매핑 포함)
  const runVerifyReport = async (resolvedMap) => {
    const fd = new FormData();
    fd.append('s1file', s1File);
    fd.append('startDate', startDate.format('YYYY-MM-DD'));
    fd.append('endDate', endDate.format('YYYY-MM-DD'));
    fd.append('selectedOption', selectedOption || 'All');
    if (resolvedMap) fd.append('resolutions', JSON.stringify(resolvedMap));
    const { data } = await axios.post(buildApiUrl('/api/overtime-verify/report'), fd);
    if (data.downloadUrl) {
      await downloadFile(data.downloadUrl);
    }
  };

  // 업로드 → 동명이인 확인 → (있으면) 모달, (없으면) 바로 생성
  const handleVerify = async () => {
    if (!s1File) { message.warning('에스원 출퇴근 엑셀을 업로드하세요.'); return; }
    if (!startDate || !endDate) { message.warning('시작일과 종료일을 선택하세요.'); return; }
    setVerifying(true);
    try {
      const fd = new FormData();
      fd.append('s1file', s1File);
      fd.append('startDate', startDate.format('YYYY-MM-DD'));
      fd.append('endDate', endDate.format('YYYY-MM-DD'));
      fd.append('selectedOption', selectedOption || 'All');
      const { data } = await axios.post(buildApiUrl('/api/overtime-verify/analyze'), fd);
      if (data.conflicts && data.conflicts.length > 0) {
        setConflicts(data.conflicts);
        setResolutions({});
        setConflictOpen(true);
      } else {
        await runVerifyReport(null);
        message.success('에스원 대조 요약이 생성되었습니다.');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '에스원 대조 중 오류가 발생했습니다.');
    } finally {
      setVerifying(false);
    }
  };

  // 동명이인 매핑 확정 후 생성
  const handleConflictConfirm = async () => {
    setVerifying(true);
    try {
      await runVerifyReport(resolutions);
      setConflictOpen(false);
      message.success('에스원 대조 요약이 생성되었습니다.');
    } catch (e) {
      message.error(e.response?.data?.message || '생성 중 오류가 발생했습니다.');
    } finally {
      setVerifying(false);
    }
  };

  // 파트 데이터를 API에서 가져오는 함수
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('');
        const allOption = { partNameKor: '전체', partNameEng: 'All' };
        setOptions([allOption, ...response.data]);
      } catch (error) {
        console.error('There was an error fetching the part names!', error);
      }
    };

    fetchData();
  }, []);

  // 날짜 선택 핸들러
  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange = (date) => setEndDate(date);

  // 체크박스 핸들러
  const handleCheckboxChange = (e) => setIsOvertimeChecked(e.target.checked);

  // 옵션 선택 핸들러
  const handleOptionChange = (value) => setSelectedOption(value);

  // 저장 버튼 활성화 여부 판단
  const isSaveButtonDisabled = () => !(startDate && endDate && (!isOvertimeChecked || (isOvertimeChecked && selectedOption)));

  // 저장 버튼 클릭 핸들러
  const handleSave = () => {
    const payload = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      isOvertimeChecked,
      selectedOption
    };

    const apiEndpoint = isOvertimeChecked ? buildApiUrl('/api/overtime-report-submit') : buildApiUrl('/api/overtime-report');
    
    axios.post(apiEndpoint, payload)
      .then(response => {
        if (isOvertimeChecked) {
          const { downloadUrl } = response.data;
          downloadFile(downloadUrl)
        } else {
          const { downloadUrl } = response.data;
          downloadFile(downloadUrl)
        }
      })
      .catch(error => {
        console.error(`There was an error submitting the data ${isOvertimeChecked ? 'with' : 'without'} overtime!`, error);
        message.error('엑셀 파일 생성 중 오류가 발생했습니다.');
      });
  };

  return (
    <ConfigProvider locale={ko_KR}>
      <div className="common-container">
        <h1>야근 보고서</h1>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="시작일">
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  style={{ width: '100%' }}
                  placeholder="시작일을 선택하세요"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="종료일">
                <DatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  style={{ width: '100%' }}
                  placeholder="종료일을 선택하세요"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Checkbox onChange={handleCheckboxChange} checked={isOvertimeChecked}>
              제출양식으로 저장
            </Checkbox>
          </Form.Item>
          {isOvertimeChecked && (
            <Form.Item label="파트를 선택하세요">
              <Select
                value={selectedOption}
                onChange={handleOptionChange}
                style={{ width: '100%', maxWidth: 300 }}
                placeholder="파트를 선택하세요"
              >
                {options.map((option, index) => (
                  <Option key={index} value={option.partNameEng}>{option.partNameKor}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={isSaveButtonDisabled()}
            >
              저장
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <Card
          size="small"
          title="에스원 출퇴근 대조 요약"
          style={{ marginTop: 8 }}
          extra={<Button size="small" icon={<SettingOutlined />} onClick={openRules}>대조 규칙 설정</Button>}
        >
          <Space wrap>
            <Upload
              accept=".xlsx"
              maxCount={1}
              beforeUpload={(file) => { setS1File(file); return false; }}
              onRemove={() => setS1File(null)}
            >
              <Button icon={<UploadOutlined />}>에스원 엑셀 선택</Button>
            </Upload>
            <Button type="primary" onClick={handleVerify} loading={verifying} disabled={!s1File}>
              대조 요약 생성
            </Button>
            <Button onClick={openNameUnify} loading={nameLoading} disabled={!s1File}>
              에스원 기준 이름 통일
            </Button>
          </Space>
        </Card>

        <Modal
          title="에스원 기준 직원 이름 통일"
          open={nameModalOpen}
          onOk={applyNames}
          onCancel={() => setNameModalOpen(false)}
          confirmLoading={nameSaving}
          okText="선택 항목 DB 저장"
          cancelText="닫기"
          width={720}
        >
          <Typography.Paragraph type="secondary">
            에스원에 일치하는 이름이 없는 DB 직원 {unmatchedList.length}명입니다. 각 직원에 해당하는 에스원 이름을 선택하면,
            <b> "원래 한글이름 + 에스원 이름"</b>으로 저장되어 이후 대조에서 매칭됩니다.
          </Typography.Paragraph>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {unmatchedList.map((u) => (
              <Row key={u.employee_no} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                <Col span={7}>
                  <Typography.Text strong>{u.name}</Typography.Text> <Typography.Text type="secondary">({u.part})</Typography.Text>
                </Col>
                <Col span={11}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="에스원 이름 선택"
                    style={{ width: '100%' }}
                    value={nameSel[u.employee_no]}
                    onChange={(v) => setNameSel((prev) => ({ ...prev, [u.employee_no]: v }))}
                    filterOption={(input, opt) => String(opt.value).toLowerCase().includes(input.toLowerCase())}
                  >
                    {s1Pool.map((p) => <Option key={p.s1_name} value={p.s1_name}>{p.s1_name}</Option>)}
                  </Select>
                </Col>
                <Col span={6}>
                  {nameSel[u.employee_no]
                    ? <Typography.Text type="success">→ {u.ko_part} {nameSel[u.employee_no]}</Typography.Text>
                    : <Typography.Text type="secondary">미선택</Typography.Text>}
                </Col>
              </Row>
            ))}
          </div>
        </Modal>

        <Modal
          title="동명이인 매핑 확인"
          open={conflictOpen}
          onOk={handleConflictConfirm}
          onCancel={() => setConflictOpen(false)}
          confirmLoading={verifying}
          okText="이대로 생성"
          cancelText="취소"
          width={640}
        >
          <Typography.Paragraph type="secondary">
            같은 이름의 직원이 여럿입니다. 각 직원이 에스원의 어느 사람인지 선택하세요. (미선택 시 해당 직원은 미매칭 처리)
          </Typography.Paragraph>
          {conflicts.map((cf) => (
            <div key={cf.norm_name} style={{ marginBottom: 16 }}>
              <b>{cf.norm_name}</b>
              {cf.ours.map((o) => (
                <div key={o.employee_no} style={{ margin: '6px 0 6px 12px' }}>
                  <Typography.Text>우리 직원 {o.name} (사번 {o.employee_no}, {o.part}) →</Typography.Text>
                  <Radio.Group
                    style={{ marginLeft: 8 }}
                    value={resolutions[o.employee_no]}
                    onChange={(e) => setResolutions((prev) => ({ ...prev, [o.employee_no]: e.target.value }))}
                  >
                    {cf.s1_candidates.map((s) => (
                      <Radio key={s.empId} value={s.empId}>{s.name} ({s.empId})</Radio>
                    ))}
                  </Radio.Group>
                </div>
              ))}
            </div>
          ))}
        </Modal>

        <Modal
          title="대조 규칙 설정 (모든 계정 공유)"
          open={rulesModalOpen}
          onOk={saveRules}
          onCancel={() => setRulesModalOpen(false)}
          confirmLoading={rulesSaving}
          okText="저장"
          cancelText="닫기"
          width={860}
        >
          <Typography.Paragraph type="secondary">
            위에서부터 순서대로 조건을 검사해, <b>먼저 맞는 규칙의 색/라벨</b>을 적용합니다. (우선순위 = 목록 순서)
            <br />퇴근시각 차이 = 에스원(또는 내부) 실제 퇴근 − 보고서 종료 (분). 빈칸은 무한(제한 없음).
          </Typography.Paragraph>
          <div style={{ display: 'grid', gridTemplateColumns: '28px 130px 1fr 110px 1fr 96px', gap: 8, alignItems: 'center', fontWeight: 600, marginBottom: 6 }}>
            <span>#</span><span>조건</span><span>범위(분)</span><span>색상</span><span>라벨</span><span>순서/삭제</span>
          </div>
          {rules.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 130px 1fr 110px 1fr 96px', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <span>{i + 1}</span>
              <Select value={r.type} onChange={(v) => updateRule(i, { type: v })} style={{ width: '100%' }}>
                {Object.entries(RULE_TYPE_LABEL).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
              </Select>
              {r.type === 'diff' ? (
                <Space.Compact>
                  <InputNumber placeholder="이상" value={r.min} onChange={(v) => updateRule(i, { min: v })} style={{ width: '50%' }} />
                  <InputNumber placeholder="이하" value={r.max} onChange={(v) => updateRule(i, { max: v })} style={{ width: '50%' }} />
                </Space.Compact>
              ) : <Typography.Text type="secondary">(해당 상태)</Typography.Text>}
              <Select value={r.color} onChange={(v) => updateRule(i, { color: v })} style={{ width: '100%' }}>
                {colorOpts.map((c) => (
                  <Option key={c} value={c}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, background: COLOR_META[c]?.hex, border: '1px solid #ccc', marginRight: 6, verticalAlign: 'middle' }} />
                    {COLOR_META[c]?.label || c}
                  </Option>
                ))}
              </Select>
              <Input value={r.label} onChange={(e) => updateRule(i, { label: e.target.value })} placeholder="라벨" />
              <Space size={2}>
                <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveRule(i, -1)} disabled={i === 0} />
                <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRule(i)} />
              </Space>
            </div>
          ))}
          <Button icon={<PlusOutlined />} onClick={addRule} style={{ marginTop: 8 }} block>규칙 추가</Button>

          <Divider style={{ margin: '16px 0 8px' }}>변경 이력 (최근 10건)</Divider>
          {ruleHistory.length === 0 ? (
            <Typography.Text type="secondary">변경 이력이 없습니다.</Typography.Text>
          ) : (
            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
              {ruleHistory.map((h) => (
                <Row key={h.id} align="middle" gutter={8} style={{ marginBottom: 4 }}>
                  <Col span={7}><Typography.Text type="secondary">{h.changed_at}</Typography.Text></Col>
                  <Col span={9}><Typography.Text strong>{h.changed_by || '알수없음'}</Typography.Text></Col>
                  <Col span={4}><Typography.Text type="secondary">규칙 {h.rules?.length || 0}개</Typography.Text></Col>
                  <Col span={4}>
                    <Button size="small" onClick={() => revertTo(h.rules)}>되돌리기</Button>
                  </Col>
                </Row>
              ))}
            </div>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default AttendanceReport;
